// Steps/Step2Counselees.tsx
import { useState } from 'react';
import {
  Stack, Group, Text, Avatar, Card, Badge,
  ActionIcon, ScrollArea, Loader, Center, Divider, Select, Button,
} from '@mantine/core';
import { IconSearch, IconUserPlus, IconTrash } from '@tabler/icons-react';
import { fullName, getInitials } from '../types';
import type { WizardState } from '../types';
import { TextInput } from '@/components';

export default function Step2Counselees(props: WizardState) {
  const {
    search, setSearch, results, searching, staged,
    addCounselee, removeCounselee, setMain, isStaged,
    allMemberTypes, creatingMember, createMember,
  } = props;

  const [showCreate, setShowCreate] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName]   = useState('');
  const [email, setEmail]         = useState('');
  const [cellNumber, setCellNumber] = useState('');
  const [typeID, setTypeID]       = useState<string | null>(null);

  const resetCreateForm = () => {
    setFirstName(''); setLastName(''); setEmail(''); setCellNumber(''); setTypeID(null);
    setShowCreate(false);
  };

  const handleCreate = async () => {
    if (!firstName.trim() || !lastName.trim() || !email.trim() || !typeID) return;
    const created = await createMember({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim(),
      cellNumber: cellNumber.trim() || null,
      typeID: Number(typeID),
    });
    if (created) resetCreateForm();
  };

  const noResults = !searching && search.trim().length > 0 && results.length === 0;

  return (
    <Stack gap="md">
      <TextInput
        placeholder="Search members by name…"
        leftSection={<IconSearch size={16} />}
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
      />

      {searching && <Center py="sm"><Loader size="sm" /></Center>}

      {!searching && results.length > 0 && (
        <ScrollArea.Autosize mah={250}>
          <Stack gap="xs">
            {results.map((m) => {
              const added = isStaged(m);
              return (
                /* The whole card adds the member. An already-added row is inert, so a second
                   click cannot stage a duplicate. */
                <Card
                  key={m.memberID}
                  withBorder
                  radius="md"
                  padding="sm"
                  onClick={added ? undefined : () => addCounselee(m, staged.length === 0)}
                  role={added ? undefined : 'button'}
                  tabIndex={added ? undefined : 0}
                  onKeyDown={(e) => {
                    if (added) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      addCounselee(m, staged.length === 0);
                    }
                  }}
                  style={{ cursor: added ? 'default' : 'pointer' }}
                >
                  <Group justify="space-between" wrap="nowrap">
                    <Group gap="sm" wrap="nowrap">
                      <Avatar src={m.avatar ?? undefined} radius="xl" size={40}>
                        {getInitials(m)}
                      </Avatar>
                      <Stack gap={0}>
                        <Text size="sm" fw={600}>{fullName(m)}</Text>
                        {m.email && <Text size="xs" c="dimmed">{m.email}</Text>}
                      </Stack>
                    </Group>
                    {added ? (
                      <Badge color="gray" variant="light">Added</Badge>
                    ) : (
                      <ActionIcon variant="light" color="green" component="div" aria-hidden>
                        <IconUserPlus size={16} />
                      </ActionIcon>
                    )}
                  </Group>
                </Card>
              );
            })}
          </Stack>
        </ScrollArea.Autosize>
      )}

      {noResults && !showCreate && (
        <Card withBorder radius="md" padding="sm">
          <Group justify="space-between">
            <Text size="sm" c="dimmed">No members found for "{search}".</Text>
            <Button size="xs" variant="light" onClick={() => setShowCreate(true)}>
              Create New Member
            </Button>
          </Group>
        </Card>
      )}

      {showCreate && (
        <Card withBorder radius="md" padding="sm">
          <Stack gap="sm">
            <Text size="sm" fw={600}>New Member</Text>
            <Group grow>
              <TextInput
                placeholder="First name"
                value={firstName}
                onChange={(e) => setFirstName(e.currentTarget.value)}
              />
              <TextInput
                placeholder="Last name"
                value={lastName}
                onChange={(e) => setLastName(e.currentTarget.value)}
              />
            </Group>
             <Group grow>
            <TextInput
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
            />
            <TextInput
              placeholder="Cell number"
              value={cellNumber}
              onChange={(e) => setCellNumber(e.currentTarget.value)}
            />
            </Group>
            <Select
              placeholder="Member type"
              data={allMemberTypes.map((t) => ({ value: String(t.typeID), label: t.name }))}
              value={typeID}
              onChange={setTypeID}
              searchable
              clearable
            />
            <Group justify="flex-end" gap="xs">
              <Button variant="subtle" size="xs" onClick={resetCreateForm}>
                Cancel
              </Button>
              <Button
                size="xs"
                loading={creatingMember}
                disabled={!firstName.trim() || !lastName.trim() || !email.trim() || !typeID}
                onClick={handleCreate}
              >
                Create & Add
              </Button>
            </Group>
          </Stack>
        </Card>
      )}

      <Divider label={`Added Counselees (${staged.length})`} labelPosition="center" />

      {staged.length === 0 ? (
        <Text size="sm" c="dimmed" ta="center">No counselees added yet.</Text>
      ) : (
        <Stack gap="xs">
          {staged.map((s) => (
            <Card key={s.member.memberID} withBorder radius="md" padding="sm">
              <Group justify="space-between" wrap="nowrap">
                <Group gap="sm" wrap="nowrap">
                  <Avatar src={s.member.avatar ?? undefined} radius="xl" size={40}>
                    {getInitials(s.member)}
                  </Avatar>
                  <Stack gap={0}>
                    <Text size="sm" fw={600}>{fullName(s.member)}</Text>
                    {s.member.email && <Text size="xs" c="dimmed">{s.member.email}</Text>}
                  </Stack>
                </Group>
                <Group gap={6}>
                  {s.isMain ? (
                    <Badge color="blue" variant="filled">Main</Badge>
                  ) : (
                    <Badge
                      color="gray"
                      variant="light"
                      style={{ cursor: 'pointer' }}
                      onClick={() => setMain(s.member.memberID)}
                    >
                      Set as Main
                    </Badge>
                  )}
                  <ActionIcon
                    variant="light"
                    color="red"
                    onClick={() => removeCounselee(s.member.memberID)}
                  >
                    <IconTrash size={16} />
                  </ActionIcon>
                </Group>
              </Group>
            </Card>
          ))}
        </Stack>
      )}
    </Stack>
  );
}